import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import {
  FaAward,
  FaBookOpen,
  FaChevronLeft,
  FaClipboardCheck,
  FaClock,
  FaCommentDots,
  FaEnvelope,
  FaHeadset,
  FaMapMarkerAlt,
  FaShieldAlt,
  FaUserTie,
  FaUsers,
} from "react-icons/fa";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import HubHomeFooter from "../../components/hub/HubHomeFooter";
import { fetchPublicCmsPage } from "../../services/cms.service";
import { buildContactCmsSections } from "../../utils/contactPageCms";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import contactHeroIllustration from "../../assets/images/contact/hero-illustration.png";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/contact.scss";

const HERO_TRUST_ITEMS = [
  { icon: FaClock, label: "Response within", value: "24 business hours" },
  { icon: FaUserTie, label: "Dedicated", value: "B2B support team" },
  { icon: FaShieldAlt, label: "Trusted by", value: "50,000+ businesses" },
];

const HELP_CARDS = [
  {
    id: "ticket",
    icon: FaHeadset,
    tone: "blue",
    title: "Raise a Ticket",
    desc: "Get technical help or account support.",
    linkLabel: "Raise Ticket",
    to: "/help-center/raise-ticket",
  },
  {
    id: "dispute",
    icon: FaClipboardCheck,
    tone: "orange",
    title: "Submit a Dispute",
    desc: "Resolve issues with orders, payments or suppliers.",
    linkLabel: "Submit Dispute",
    to: "/submit-dispute",
  },
  {
    id: "help",
    icon: FaBookOpen,
    tone: "green",
    title: "Help Center",
    desc: "Browse FAQs, articles and guides.",
    linkLabel: "Visit Help Center",
    to: "/help-center",
  },
  {
    id: "feedback",
    icon: FaCommentDots,
    tone: "purple",
    title: "Provide Feedback",
    desc: "Share your suggestions and help us improve.",
    linkLabel: "Give Feedback",
    to: "/feedback",
  },
];

const WHY_ITEMS = [
  {
    icon: FaClock,
    tone: "orange",
    title: "24 Hours Response Time",
    desc: "We respond within 24 business hours.",
  },
  {
    icon: FaShieldAlt,
    tone: "blue",
    title: "Secure & Reliable",
    desc: "Your information is safe and protected.",
  },
  {
    icon: FaUsers,
    tone: "green",
    title: "Expert Support",
    desc: "Our team of experts is ready to help you.",
  },
  {
    icon: FaAward,
    tone: "purple",
    title: "Customer First",
    desc: "Your success is our top priority.",
  },
];

const MAP_EMBED_URL =
  "https://maps.google.com/maps?q=TrocaExPro+Business+Hub+MG+Road+Bengaluru&t=&z=15&ie=UTF8&iwloc=&output=embed";

const MAP_DIRECTIONS_URL =
  "https://www.google.com/maps/dir/?api=1&destination=TrocaExPro+Business+Hub,+4th+Floor,+MG+Road,+Bengaluru,+Karnataka+560001,+India";

function ContactCtaIllustration() {
  return (
    <svg
      className="hub-contact-cta__art"
      viewBox="0 0 140 110"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <ellipse cx="70" cy="98" rx="48" ry="7" fill="#F0C4A0" opacity="0.45" />
      <path
        d="M22 48h76c4.4 0 8 3.6 8 8v30c0 4.4-3.6 8-8 8H22c-4.4 0-8-3.6-8-8V56c0-4.4 3.6-8 8-8Z"
        fill="#FF7A1A"
      />
      <path d="M14 56l46 28 46-28" fill="#FF9A4A" />
      <path d="M14 56l46 28 46-28" stroke="#FFE0C2" strokeWidth="2.5" strokeLinejoin="round" fill="none" />
      <rect x="38" y="22" width="52" height="42" rx="4" fill="#FFF8F1" stroke="#FFB57A" strokeWidth="2" />
      <path d="M48 36h32M48 46h24M48 56h28" stroke="#FFB57A" strokeWidth="2.5" strokeLinecap="round" />
      <rect x="58" y="14" width="44" height="34" rx="3" fill="#FFE8D2" transform="rotate(8 58 14)" />
      <path
        d="M66 24h24M66 32h18"
        stroke="#FF9A4A"
        strokeWidth="2"
        strokeLinecap="round"
        transform="rotate(8 58 14)"
      />
    </svg>
  );
}

export default function Contact() {
  const [searchParams] = useSearchParams();
  const [cmsPage, setCmsPage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchPublicCmsPage("CONTACT_US");
        if (!cancelled) setCmsPage(data);
      } catch {
        if (!cancelled) setCmsPage(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const cms = useMemo(() => buildContactCmsSections(cmsPage), [cmsPage]);

  if (String(searchParams.get("enquiry") || "").trim().toLowerCase() === "technical") {
    return <Navigate to="/help-center/raise-ticket" replace />;
  }

  const supportEmail =
    cms.directEmails?.find((row) => /support/i.test(row.label) || /support@/i.test(row.email))?.email ||
    cms.directEmails?.[0]?.email ||
    "support@trocanet.com";

  const officeAddress =
    cms.officeAddress ||
    "TrocaExPro Business Hub, 4th Floor, MG Road, Bengaluru, Karnataka 560001, India";

  return (
    <div className="hub-contact-page">
      <B2BHubHomeHeader />

      <main className="hub-contact-page__main">
        <section className="hub-contact-hero" aria-labelledby="hub-contact-title">
          <div className="hub-contact-hero__pattern" aria-hidden />
          <div className="hub-contact-hero__arcs" aria-hidden />
          <div className="container-fluid hub-contact-hero__inner">
            <div className="hub-contact-breadcrumb-row">
              <nav className="hub-contact-breadcrumb" aria-label="Breadcrumb">
                <Link to="/home">Home</Link>
                <span aria-hidden>&gt;</span>
                <span aria-current="page">Contact Us</span>
              </nav>
              <Link to="/home" className="hub-contact-back">
                <FaChevronLeft aria-hidden /> Back to Home
              </Link>
            </div>
            <div className="hub-contact-hero__content">
              <div className="hub-contact-hero__copy">
                <h1 id="hub-contact-title" className="hub-contact-hero__title">
                  We&apos;re here to help your <span>B2B business</span> grow
                </h1>
                <p className="hub-contact-hero__lead">
                  Whether you have a question about sourcing, suppliers, RFQs, logistics or platform
                  features – our team is ready to help you.
                </p>

                <ul className="hub-contact-hero__trust" aria-label="Support highlights">
                  {HERO_TRUST_ITEMS.map((item) => {
                    const Icon = item.icon;
                    return (
                      <li key={item.value}>
                        <span className="hub-contact-hero__trust-icon">
                          <Icon aria-hidden />
                        </span>
                        <span className="hub-contact-hero__trust-text">
                          <small>{item.label}</small>
                          <strong>{item.value}</strong>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="hub-contact-hero__visual-wrap">
                <img
                  src={
                    cms.heroBackgroundUrl
                      ? resolvePublicFileUrl(cms.heroBackgroundUrl)
                      : contactHeroIllustration
                  }
                  alt=""
                  className="hub-contact-hero__illustration"
                  width={493}
                  height={262}
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="hub-contact-help" aria-labelledby="hub-contact-help-title">
          <div className="container-fluid hub-contact-section__inner">
            <h2 id="hub-contact-help-title" className="hub-contact-section__title">
              What can we help you with?
            </h2>
            <div className="hub-contact-help__grid">
              {HELP_CARDS.map((card) => {
                const Icon = card.icon;
                return (
                  <article key={card.id} className={`hub-contact-help-card hub-contact-help-card--${card.tone}`}>
                    <span className="hub-contact-help-card__icon" aria-hidden>
                      <Icon />
                    </span>
                    <h3>{card.title}</h3>
                    <p>{card.desc}</p>
                    <Link to={card.to} className="hub-contact-help-card__btn">
                      {card.linkLabel} <span aria-hidden>→</span>
                    </Link>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="hub-contact-touch" aria-labelledby="hub-contact-touch-title">
          <div className="container-fluid hub-contact-section__inner">
            <h2 id="hub-contact-touch-title" className="hub-contact-section__title">
              Get in touch
            </h2>
            <div className="hub-contact-touch__panel">
              <div className="hub-contact-touch__item">
                <span className="hub-contact-touch__icon hub-contact-touch__icon--blue" aria-hidden>
                  <FaEnvelope />
                </span>
                <h3>Email Us</h3>
                <a href={`mailto:${supportEmail}`} className="hub-contact-touch__value">
                  {supportEmail}
                </a>
                <p>We usually respond within 24 business hours.</p>
              </div>

              <div className="hub-contact-touch__item">
                <span className="hub-contact-touch__icon hub-contact-touch__icon--orange" aria-hidden>
                  <FaMapMarkerAlt />
                </span>
                <h3>Our Office</h3>
                <p className="hub-contact-touch__address">{officeAddress}</p>
                <a
                  href={MAP_DIRECTIONS_URL}
                  className="hub-contact-touch__link hub-contact-touch__link--orange"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View on Map <span aria-hidden>→</span>
                </a>
              </div>

              <div className="hub-contact-touch__item">
                <span className="hub-contact-touch__icon hub-contact-touch__icon--purple" aria-hidden>
                  <FaClock />
                </span>
                <h3>Business Hours</h3>
                <p className="hub-contact-touch__hours">
                  <strong>Mon – Sat:</strong> 9:00 AM – 7:00 PM IST
                  <br />
                  <strong>Sunday:</strong> Closed
                  <br />
                  <span className="hub-contact-touch__note">(Excluding public holidays)</span>
                </p>
              </div>
            </div>

            <div className="hub-contact-map-block">
              <iframe
                title="TrocaExPro Business Hub location"
                src={MAP_EMBED_URL}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
              <aside className="hub-contact-map-card">
                <strong>TrocaExPro Business Hub</strong>
                <p>4th Floor, MG Road, Bengaluru, Karnataka 560001, India</p>
                <a href={MAP_DIRECTIONS_URL} target="_blank" rel="noopener noreferrer">
                  Get Directions <span aria-hidden>→</span>
                </a>
              </aside>
            </div>
          </div>
        </section>

        <section className="hub-contact-why" aria-labelledby="hub-contact-why-title">
          <div className="container-fluid hub-contact-section__inner">
            <h2 id="hub-contact-why-title" className="hub-contact-section__title">
              Why businesses choose Trocanet
            </h2>
            <div className="hub-contact-why__grid">
              {WHY_ITEMS.map((item) => {
                const Icon = item.icon;
                return (
                  <article key={item.title} className="hub-contact-why-item">
                    <span className={`hub-contact-why-item__icon hub-contact-why-item__icon--${item.tone}`} aria-hidden>
                      <Icon />
                    </span>
                    <div>
                      <h3>{item.title}</h3>
                      <p>{item.desc}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="hub-contact-cta" aria-labelledby="hub-contact-cta-title">
          <div className="container-fluid hub-contact-section__inner">
            <div className="hub-contact-cta__banner">
              <ContactCtaIllustration />
              <div className="hub-contact-cta__copy">
                <h2 id="hub-contact-cta-title">Still can&apos;t find what you&apos;re looking for?</h2>
                <p>
                  Visit our Help Center for answers to common questions or browse our guides and
                  resources.
                </p>
              </div>
              <Link to="/help-center" className="hub-contact-cta__btn">
                Visit Help Center <span aria-hidden>→</span>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <HubHomeFooter />
    </div>
  );
}
